// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// THE MODAL DISMISSAL CONTRACT.
//
// A visitor who opens an overlay must ALWAYS be able to get out of it. There is
// no such thing as an overlay with one way out. Every overlay created here gets
// all four exits, and they are not alternatives:
//
//   1. A close control (x) inside the overlay, >= 44x44 CSS px of hit area,
//      a real <button> so Enter and Space reach it.
//   2. Click or tap anywhere outside the panel -- implemented as `pointerdown`
//      on the scrim, which covers mouse, touch and pen in ONE handler and fires
//      where a synthetic click would not.
//   3. Escape, from anywhere on the page.
//   4. A scrim that is actually reachable: position:fixed, inset:0, below the
//      panel but above the page, and never pointer-events:none.
//
// Two rules that look like details and are not:
//   - ONE EVENT FAMILY PER CONTROL. Binding both `touchstart` and `click` to
//     the same control makes a single tap fire twice: it closes and instantly
//     reopens, which a user experiences as "the close button does nothing".
//     The close button uses `click` alone (so keyboards work); the scrim uses
//     `pointerdown` alone.
//   - Dismissal is NEVER gated on re-tapping the control that opened the
//     overlay, because on a phone the overlay usually covers that control.
//
// The commonest cause of "the close button doesn't work" is none of the above:
// it is the missing `[hidden]` guard in CSS, which leaves the overlay displayed
// no matter what the handler sets. See styles.css. These overlays sidestep that
// class of bug entirely by DETACHING from the DOM on close rather than toggling
// a hidden attribute -- which also fixes the iOS bug where a closed drawer
// parked at translateX(100%) is still a real box that scrolls the page
// sideways.

let openCount = 0;

function lockScroll(): void {
  openCount++;
  document.body.style.overflow = 'hidden';
}

function releaseScroll(): void {
  openCount = Math.max(0, openCount - 1);
  // Derived from a counter that can never go negative, rather than a module
  // flag that can drift and weld overflow:hidden onto the page forever.
  if (openCount === 0) document.body.style.overflow = '';
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface OverlayOptions {
  /** Accessible title. Also rendered as the heading. */
  title: string;
  /** Rendered into the body. Caller owns escaping. */
  body: string | HTMLElement;
  /** 'modal' centres a dialog; 'drawer' slides in from the right. */
  kind?: 'modal' | 'drawer';
  /** Extra class on the panel. */
  className?: string;
  onClose?: () => void;
}

export interface OverlayHandle {
  close(): void;
  panel: HTMLElement;
  scrim: HTMLElement;
}

export function openOverlay(opts: OverlayOptions): OverlayHandle {
  const kind = opts.kind ?? 'modal';
  const trigger = document.activeElement as HTMLElement | null;

  const scrim = document.createElement('div');
  scrim.className = 'scrim';

  const panel = document.createElement('div');
  panel.className = kind === 'drawer' ? 'drawer' : 'modal';
  if (opts.className) panel.classList.add(opts.className);
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');

  const titleId = `ov-title-${Math.random().toString(36).slice(2, 9)}`;
  panel.setAttribute('aria-labelledby', titleId);

  const head = document.createElement('div');
  head.className = 'overlay-head';
  const h = document.createElement('h2');
  h.id = titleId;
  h.textContent = opts.title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'close-btn';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '✕';
  head.append(h, close);

  const body = document.createElement('div');
  body.className = 'overlay-body';
  if (typeof opts.body === 'string') body.innerHTML = opts.body;
  else body.appendChild(opts.body);

  panel.append(head, body);

  // A modal needs a positioning wrapper; the wrapper is pointer-events:none so
  // the scrim underneath stays reachable everywhere the panel is not.
  let mount: HTMLElement = panel;
  if (kind === 'modal') {
    const wrap = document.createElement('div');
    wrap.className = 'modal-wrap';
    wrap.appendChild(panel);
    mount = wrap;
  }

  document.body.append(scrim, mount);
  lockScroll();

  let closed = false;
  const doClose = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey, true);
    // DETACH, do not hide. A hidden-but-present overlay is a real box on iOS
    // Safari and can still scroll the page sideways.
    scrim.remove();
    mount.remove();
    releaseScroll();
    opts.onClose?.();
    if (trigger && document.contains(trigger)) trigger.focus();
  };

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.stopPropagation();
      doClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Exit 1: the close control. `click` alone -- it is what a keyboard produces
  // for a <button>, and pairing it with a pointer event would double-fire.
  close.addEventListener('click', doClose);

  // Exit 2 + 4: the scrim, via pointerdown so mouse, touch and pen all work.
  scrim.addEventListener('pointerdown', doClose);
  // For a centred modal the wrapper also covers the viewport, so a tap on the
  // wrapper but outside the panel must close too.
  if (kind === 'modal') {
    mount.addEventListener('pointerdown', (e) => {
      if (!panel.contains(e.target as Node)) doClose();
    });
  }

  // Exit 3: Escape, captured so a view's own key handling cannot swallow it.
  document.addEventListener('keydown', onKey, true);

  // Move focus into the dialog so a screen reader lands inside it.
  (panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel).focus?.();

  return { close: doClose, panel, scrim };
}

/** Test seam: how many overlays believe they are open. */
export function openOverlayCount(): number {
  return openCount;
}
